/**
 * The view model and URL codec (1.N-2 deliverable 3). A view is `{q, filters, sort, columns}`;
 * the URL search is its canonical form under TanStack Router's default JSON serialization.
 * Pure: nothing here throws on any input.
 */
import {
  CATALOGUE_KIND_OPERATORS,
  CatalogueFilterV1,
  type CatalogueQueryV1,
  canonicalCatalogueQuery,
} from '@codex/contracts';
import { type ColumnDef, defaultColumnKeys } from './columns.js';
import { valueFits } from './filters.js';

export interface SortKey {
  readonly column: string;
  readonly direction: 'asc' | 'desc';
}

export type Filter = CatalogueFilterV1;

export interface CatalogueView {
  readonly q: string;
  readonly filters: readonly Filter[];
  readonly sort: readonly SortKey[];
  /** Visible column keys in order; `title` is always first. */
  readonly columns: readonly string[];
}

/**
 * What the URL carries: each key only when it differs from the default. A key set to `undefined`
 * is absent; `parseCatalogueSearch` names `q`, `filters`, `sort` and `columns` so that TanStack
 * Router's `{...rawSearch, ...validateSearch(rawSearch)}` merge cannot keep a rejected raw value.
 */
export interface CatalogueSearch {
  q?: string | undefined;
  filters?: Filter[] | undefined;
  sort?: SortKey[] | undefined;
  columns?: string[] | undefined;
  /**
   * How many parameters `parseCatalogueSearch` rejected; never written back by the page. The
   * router's mount-time canonicalization does write it (`?sort=garbage` becomes `?bad=1`), so a
   * raw positive integer `bad` is carried into the next parse's count.
   */
  bad?: number | undefined;
}

export const MAX_Q = 200;
export const MAX_FILTERS = 20;
export const MAX_SORT = 5;
export const MAX_COLUMNS = 64;
export const PAGE_SIZE = 100;

const COLUMN_PATTERN = /^[a-zA-Z][a-zA-Z0-9_]{0,63}$/;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** A `bad` count: a positive integer. Anything else in that key is not a count. */
const isCount = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value > 0;

function asSortKey(value: unknown): SortKey | null {
  if (!isRecord(value)) return null;
  const keys = Object.keys(value);
  if (keys.length !== 2) return null;
  const { column, direction } = value;
  if (typeof column !== 'string' || !COLUMN_PATTERN.test(column)) return null;
  if (direction !== 'asc' && direction !== 'desc') return null;
  return { column, direction };
}

/**
 * Keeps each parameter only if its JSON shape fits, else drops it (counted in `bad`). Never throws.
 * `q`, `filters`, `sort` and `columns` are always in the answer, `undefined` when absent or
 * rejected: the router spreads this answer over the raw search, so a key left out would let the
 * raw value through.
 *
 * `bad` is named too, but it does not wipe a count already in the URL: on mount the router writes
 * the merged search back (`?sort=garbage` becomes `?bad=1`) and parses that URL again, so a raw
 * positive integer `bad` is added to this parse's count and the page still notices the reset.
 * Any other raw `bad` (a forged `?bad=abc`, a negative or fractional number) is not a count and is
 * set to `undefined` like any rejected value; `readView` would ignore it anyway.
 */
export function parseCatalogueSearch(raw: unknown): CatalogueSearch {
  const src = isRecord(raw) ? raw : {};
  const out: CatalogueSearch = {
    q: undefined,
    filters: undefined,
    sort: undefined,
    columns: undefined,
    bad: undefined,
  };
  let bad = isCount(src.bad) ? src.bad : 0;

  if (src.q !== undefined) {
    if (typeof src.q === 'string' && src.q.length <= MAX_Q) out.q = src.q;
    else bad++;
  }

  if (src.filters !== undefined) {
    const list = src.filters;
    const parsed =
      Array.isArray(list) && list.length <= MAX_FILTERS
        ? list.map((item) => CatalogueFilterV1.safeParse(item))
        : null;
    if (parsed?.every((r) => r.success)) {
      out.filters = parsed.flatMap((r) => (r.success ? [r.data] : []));
    } else bad++;
  }

  if (src.sort !== undefined) {
    const list = src.sort;
    const keys = Array.isArray(list) && list.length <= MAX_SORT ? list.map(asSortKey) : null;
    if (keys?.every((k) => k !== null)) out.sort = keys as SortKey[];
    else bad++;
  }

  if (src.columns !== undefined) {
    const list = src.columns;
    if (
      Array.isArray(list) &&
      list.length <= MAX_COLUMNS &&
      list.every((item) => typeof item === 'string')
    ) {
      out.columns = list as string[];
    } else bad++;
  }

  if (bad > 0) out.bad = bad;
  return out;
}

const isDefaultSort = (sort: readonly SortKey[]): boolean =>
  sort.length === 0 ||
  (sort.length === 1 && sort[0]?.column === 'title' && sort[0].direction === 'asc');

const sameKeys = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((key, index) => key === b[index]);

export interface ReadResult {
  readonly view: CatalogueView;
  /** How many settings were removed because they did not fit this table. */
  readonly dropped: number;
}

/**
 * The view a search names, for a table with these columns. An unknown column, an operator outside
 * `CATALOGUE_KIND_OPERATORS` for the column's kind, a value of the wrong JSON type for its operator, a duplicate sort or column key is removed and
 * counted. `title` is always visible and first; an empty column list becomes the default; a sort
 * of exactly `title` ascending is the default sort and is read as no sort.
 */
export function readView(search: CatalogueSearch, columns: readonly ColumnDef[]): ReadResult {
  const known = new Map(columns.map((column) => [column.key, column]));
  let dropped = isCount(search.bad) ? search.bad : 0;
  // A search no parser checked (a raw value the router kept) is read defensively: a list that is
  // not an array, or a q that is not a string, is absent; a long q is clamped; a bad item is
  // dropped. So the view always fits `CatalogueQueryV1` and `queryKey` cannot throw in a render.
  const listOf = (value: unknown): readonly unknown[] => {
    if (value === undefined) return [];
    if (Array.isArray(value)) return value;
    dropped++;
    return [];
  };
  let q = '';
  if (typeof search.q === 'string') {
    q = search.q.slice(0, MAX_Q);
    if (q.length < search.q.length) dropped++;
  } else if (search.q !== undefined) dropped++;

  const filters: Filter[] = [];
  for (const item of listOf(search.filters)) {
    const parsed = CatalogueFilterV1.safeParse(item);
    if (!parsed.success || filters.length >= MAX_FILTERS) {
      dropped++;
      continue;
    }
    const filter = parsed.data;
    const column = known.get(filter.column);
    const operators: readonly string[] = column ? CATALOGUE_KIND_OPERATORS[column.kind] : [];
    if (column === undefined || !operators.includes(filter.op) || !valueFits(column, filter))
      dropped++;
    else filters.push(filter);
  }

  let sort: SortKey[] = [];
  const sortSeen = new Set<string>();
  for (const item of listOf(search.sort)) {
    const key = asSortKey(item);
    if (key === null || sort.length >= MAX_SORT) {
      dropped++;
      continue;
    }
    if (!known.has(key.column) || sortSeen.has(key.column)) {
      dropped++;
      continue;
    }
    sortSeen.add(key.column);
    sort.push(key);
  }
  if (isDefaultSort(sort)) sort = [];

  const defaults = defaultColumnKeys(columns);
  // As in `parseCatalogueSearch`, a column list with any item that is not a string is absent.
  let requested = listOf(search.columns);
  if (!requested.every((key) => typeof key === 'string')) {
    dropped++;
    requested = [];
  }
  let keys: string[];
  if (requested.length === 0) keys = defaults;
  else {
    keys = [];
    const seen = new Set<string>();
    for (const key of requested) {
      if (typeof key !== 'string' || !known.has(key) || seen.has(key)) {
        dropped++;
        continue;
      }
      seen.add(key);
      keys.push(key);
    }
    keys = ['title', ...keys.filter((key) => key !== 'title')];
  }

  return {
    view: { q, filters, sort, columns: keys },
    dropped,
  };
}

/** The canonical search of a view: every default omitted. Key order is fixed. */
export function writeSearch(view: CatalogueView, columns: readonly ColumnDef[]): CatalogueSearch {
  const out: CatalogueSearch = {};
  if (view.q !== '') out.q = view.q;
  if (view.filters.length > 0) out.filters = [...view.filters];
  if (!isDefaultSort(view.sort)) out.sort = [...view.sort];
  if (!sameKeys(view.columns, defaultColumnKeys(columns))) out.columns = [...view.columns];
  return out;
}

/** A search without the transient `bad` count, for comparing with the canonical form. */
export function withoutBad(search: CatalogueSearch): CatalogueSearch {
  const { bad: _bad, ...rest } = search;
  const out: CatalogueSearch = {};
  if (rest.q !== undefined) out.q = rest.q;
  if (rest.filters !== undefined) out.filters = rest.filters;
  if (rest.sort !== undefined) out.sort = rest.sort;
  if (rest.columns !== undefined) out.columns = rest.columns;
  return out;
}

/** The part of a view the server sees. */
export function queryOf(view: CatalogueView): QueryInput {
  return {
    ...(view.q === '' ? {} : { q: view.q }),
    filters: [...view.filters],
    sort: isDefaultSort(view.sort) ? [] : [...view.sort],
  };
}

type QueryInput = Pick<CatalogueQueryV1, 'filters' | 'sort'> & { q?: string };

/** The cache key of a view's query: equal queries (any key order) share one string. */
export function queryKey(view: CatalogueView, typeKey: string): string {
  return canonicalCatalogueQuery(typeKey, queryOf(view));
}

/** The next sort when `column`'s header is pressed: ascending, descending, then off. */
export function cycleSort(sort: readonly SortKey[], column: string): SortKey[] {
  const primary: SortKey = sort[0] ?? { column: 'title', direction: 'asc' };
  if (primary.column !== column) return [{ column, direction: 'asc' }];
  if (primary.direction === 'asc') return [{ column, direction: 'desc' }];
  return [];
}

/** The view with one column shown or hidden (`title` cannot be hidden). */
export function toggleColumn(
  view: CatalogueView,
  columns: readonly ColumnDef[],
  key: string,
): CatalogueView {
  if (key === 'title') return view;
  if (view.columns.includes(key)) {
    return { ...view, columns: view.columns.filter((k) => k !== key) };
  }
  // Shown again, a column takes its place in the table's own order.
  const order = columns.map((column) => column.key);
  const at = order.indexOf(key);
  const before = view.columns.findIndex((k) => order.indexOf(k) > at);
  const next = [...view.columns];
  next.splice(before === -1 ? next.length : before, 0, key);
  return { ...view, columns: next };
}
